// Every editable config key is mapped to a specific widget so the UI
// is always safe to use — no free-form JSON editing where an admin
// could wipe a map / array and corrupt the running game.
//
// A key that is NOT in this registry automatically falls back to the
// `readonly` widget (shows the value but disables saving), so new
// keys added to the server can never accidentally ship as a raw-JSON
// box for admins to break.

export type DifficultySubType = 'percent' | 'probability' | 'number';

export type Widget =
  | { kind: 'bool' }
  | { kind: 'enum'; options: Array<{ value: string; label: string; icon?: string }> }
  | { kind: 'integer'; min?: number; max?: number; suffix?: string }
  | { kind: 'percent' }
  | { kind: 'probability' }
  | { kind: 'number'; step?: number; min?: number; max?: number; suffix?: string }
  | { kind: 'duration-ms' }
  | { kind: 'duration-sec' }
  | {
      kind: 'difficulty-map';
      sub: DifficultySubType;
      // Optional bounds — only honoured when sub === 'number'. Lets a
      // single widget cover any numeric tier-map (0-20 Stockfish skill,
      // 1-30 search-depth, 0-2 score multiplier, etc.) without
      // proliferating one-off sub-types per range.
      min?: number;
      max?: number;
      step?: number;
      suffix?: string;
      integer?: boolean;
    }
  | { kind: 'key-value-map'; keys: Array<{ key: string; label: string; icon?: string }>; valueType: 'number' | 'string' | 'probability' | 'percent' }
  | { kind: 'bet-mode-list' }
  | { kind: 'personality-weight-rules' }
  | { kind: 'personality-list' }
  | { kind: 'number-list'; min?: number; max?: number; allowZero?: boolean; placeholder?: string }
  | { kind: 'string-list'; placeholder?: string }
  | { kind: 'string'; placeholder?: string; multiline?: boolean; maxLength?: number }
  | { kind: 'checkboxes'; options: Array<{ value: number | string; label: string }> }
  | { kind: 'readonly'; reason?: string };

// ─── Per-key schema ──────────────────────────────────────────────────
// If a key is not listed here, ConfigField renders it as read-only.

export const WIDGET_SCHEMA: Record<string, Widget> = {
  // ── Simple toggles ──
  BOT_ENABLED:             { kind: 'bool' },
  AUDIO_ENABLED:           { kind: 'bool' },
  MATCH_RECORDING_ENABLED: { kind: 'bool' },
  SHOW_QUEUE_WAITING_BADGE:{ kind: 'bool' },
  ALLOW_JOKER_DISCARD_PICKUP: { kind: 'bool' },

  // Disposition picker — "refund" returns stakes when the abandon
  // window expires, "forfeit" keeps them. See ABANDON_ACTION manifest
  // for the full description.
  ABANDON_ACTION: {
    kind: 'enum',
    options: [
      { value: 'refund',  label: 'Refund both sides',     icon: 'undo' },
      { value: 'forfeit', label: 'Forfeit (no refund)',   icon: 'gavel' },
    ],
  },
  BOT_DICE_CONTROL: { kind: 'bool' },

  // ── Enums ──
  BOT_DEFAULT_MODE: {
    kind: 'enum',
    options: [
      { value: 'easy',   label: 'Easy',   icon: 'sentiment_satisfied' },
      { value: 'medium', label: 'Medium', icon: 'sentiment_neutral' },
      { value: 'hard',   label: 'Hard',   icon: 'local_fire_department' },
    ],
  },
  DRAW_RESOLUTION: {
    kind: 'enum',
    options: [
      { value: 'split_refund', label: 'Split refund (50/50 minus fee)' },
      { value: 'full_refund',  label: 'Full refund (both get bet back)' },
      { value: 'sudden_death', label: 'Sudden death (play extra rounds)' },
    ],
  },

  // ── Target-Profit engine (the single headline slider) ──
  target_profit_enabled:                { kind: 'bool' },
  // Phase E's roulette conversion (2026-09-06). Without an entry here `widgetFor` falls back to
  // `readonly`, `ConfigField` disables Save, and the owner — who is the person the plan says
  // performs this flip — cannot perform it from the admin UI at all. The backend takes `z.any()`
  // with no coercion, so a hand-rolled PUT of the string "true" would silently read as OFF; a
  // real boolean toggle is the only safe way to expose it.
  shared_tables:                        { kind: 'bool' },
  target_profit_pct:                    { kind: 'percent' },
  // ── PHASE 2 §3.2 — the bounded reserve's per-game rollout flag (2026-09-14) ──
  // A plain boolean, and a boolean is the whole of it: the flag selects between two
  // code paths and has no middle setting. Without an entry here `widgetFor` falls
  // back to `readonly`, `ConfigField` disables Save, and the flip cannot be performed
  // from the admin UI at all — the same hole `shared_tables` above was added to close.
  // The backend takes `z.any()` with no coercion, so a hand-rolled PUT of the string
  // "true" would land as the JSON string `"true"`; the engines' `cfgBool` happens to
  // accept that one spelling, but a real toggle is the only way to be sure what is
  // stored. Declared by the four games whose engines read the flag off the in-memory
  // config object (roulette, aviator, andar_bahar, teen_patti) — see those manifests.
  //
  // THERE IS DELIBERATELY NO `reserve_authority_z` / `reserve_dead_zone_z` WIDGET, and
  // no manifest declares either key. Contract §3.2 lists them as config keys, but §3.1
  // puts the same two numbers on the `game_reserve` row (`authority_bp` /
  // `dead_zone_bp`) and that is where the shipped code reads them — `reserveStore.js`
  // divides `authority_bp` by 10,000; nothing anywhere reads a config key of either
  // name. THE ROW IS AUTHORITATIVE, and it is the right home: `a_z` is the number
  // `bound_paise` and `limit_paise` were SIZED AGAINST, so a config save that moved it
  // would invalidate, from a different table, the bound it was computed with. A
  // `number` widget here would also render `Number(undefined) || 0` → 0 for a value
  // whose real setting is 0.15, i.e. an editor that shows the wrong number and then
  // saves it somewhere no code looks. §3.2 is wrong on this point; §3.1 stands.
  reserve_enabled:                      { kind: 'bool' },

  // ── Percentages (0-100 slider) ──
  PLATFORM_FEE_PERCENT:                 { kind: 'percent' },
  BOT_DICE_CONTROL_PERCENT:             { kind: 'percent' },
  BOT_HARD_DICE_CONTROL_PERCENT:        { kind: 'percent' },
  BOT_WHIFF_PERCENT_PER_TURN:           { kind: 'percent' },
  BOT_HESITATE_PERCENT:                 { kind: 'percent' },
  BOT_HESITATE_PERCENT_MULTI:           { kind: 'percent' },
  BOT_AFK_QUIT_4P_PERCENT:              { kind: 'percent' },
  BOT_AFK_QUIT_PERCENT:                 { kind: 'percent' },
  BOT_DICE_CONTROL_DISTANCE_BOOST_MAX:  { kind: 'percent' },

  // ── Probabilities (0.0-1.0 slider with 2-decimal step) ──
  BOT_CAPTURE_BRUTALITY_MIN:            { kind: 'probability' },
  BOT_CAPTURE_BRUTALITY_MAX:            { kind: 'probability' },
  BOT_CAPTURE_BRUTALITY_DISTANCE_SWING: { kind: 'probability' },
  BOT_BRUTALITY_MIN:                    { kind: 'probability' },
  BOT_BRUTALITY_MAX:                    { kind: 'probability' },
  BOT_BRUTALITY_SCORE_SWING:            { kind: 'probability' },

  // ── Durations ──
  BOT_FILL_STAGGER_MIN_MS:   { kind: 'duration-ms' },
  BOT_FILL_STAGGER_MAX_MS:   { kind: 'duration-ms' },
  BOT_HESITATE_MIN_MS:       { kind: 'duration-ms' },
  BOT_HESITATE_MAX_MS:       { kind: 'duration-ms' },
  BOT_ROLL_DELAY_MIN_MS:     { kind: 'duration-ms' },
  BOT_ROLL_DELAY_MAX_MS:     { kind: 'duration-ms' },
  BOT_MOVE_DELAY_MIN_MS:     { kind: 'duration-ms' },
  BOT_MOVE_DELAY_MAX_MS:     { kind: 'duration-ms' },
  WS_AUTH_TIMEOUT_MS:        { kind: 'duration-ms' },

  BOT_ACTIVATE_AFTER_SECONDS:   { kind: 'duration-sec' },
  TURN_SECONDS:                 { kind: 'duration-sec' },
  FIRST_TURN_GRACE_SECONDS:     { kind: 'duration-sec' },
  FIRST_MOVE_GRACE_SECONDS:     { kind: 'duration-sec' },
  URGENCY_BEEP_LEAD_SECONDS:    { kind: 'duration-sec' },
  CLIENT_TIMER_OFFSET_SECONDS:  { kind: 'duration-sec' },
  BOT_ONLINE_DRIFT_SECONDS:     { kind: 'duration-sec' },
  DISCONNECT_GRACE_SECONDS:     { kind: 'duration-sec' },
  RECONNECT_WINDOW_SECONDS:     { kind: 'duration-sec' },

  // ── Plain integers ──
  HUMAN_STRIKE_LIMIT:            { kind: 'integer', min: 1, max: 20, suffix: 'timeouts' },
  BOT_STRIKE_LIMIT:              { kind: 'integer', min: 1, max: 20, suffix: 'timeouts' },
  BOT_AFK_QUIT_STRIKE_LIMIT:     { kind: 'integer', min: 1, max: 10, suffix: 'timeouts' },
  BOT_AFK_QUIT_AFTER_TURN_MIN:   { kind: 'integer', min: 1, max: 50, suffix: 'turns' },
  BOT_AFK_QUIT_AFTER_TURN_MAX:   { kind: 'integer', min: 1, max: 50, suffix: 'turns' },
  BOT_AFK_QUIT_AFTER_ROUND_MIN:  { kind: 'integer', min: 1, max: 20, suffix: 'rounds' },
  BOT_AFK_QUIT_AFTER_ROUND_MAX:  { kind: 'integer', min: 1, max: 20, suffix: 'rounds' },
  MAX_ROUNDS:                    { kind: 'integer', min: 1, max: 11, suffix: 'rounds' },
  ROUNDS_TO_WIN:                 { kind: 'integer', min: 1, max: 11, suffix: 'rounds' },
  BOT_WHIFF_PER_GAME_MIN:        { kind: 'integer', min: 0, max: 10 },
  BOT_WHIFF_PER_GAME_MAX:        { kind: 'integer', min: 0, max: 10 },
  BOT_WHIFF_PER_MATCH_MIN:       { kind: 'integer', min: 0, max: 10 },
  BOT_WHIFF_PER_MATCH_MAX:       { kind: 'integer', min: 0, max: 10 },
  BOT_DICE_HONEST_IF_YARD_GTE:   { kind: 'integer', min: 0, max: 4, suffix: 'tokens' },
  BOT_ONLINE_BASE_MIN:           { kind: 'integer', min: 0, max: 1000, suffix: 'players' },
  BOT_ONLINE_BASE_MAX:           { kind: 'integer', min: 0, max: 1000, suffix: 'players' },
  BOT_ONLINE_DRIFT_MAX:          { kind: 'integer', min: 0, max: 20 },
  // BOT_WALLET_MIN/MAX/STEP are legacy cosmetic settings — bots now
  // have real per-bot wallets on the Bots admin page. These values
  // still drive the label for synthetic fallback bots that appear
  // only when no real bot is available.
  BOT_WALLET_MIN:                { kind: 'readonly', reason: 'Bots now carry real wallets on the Bots admin page. This fallback value only affects synthetic bots that appear if the bot roster is empty.' },
  BOT_WALLET_MAX:                { kind: 'readonly', reason: 'Bots now carry real wallets on the Bots admin page. This fallback value only affects synthetic bots that appear if the bot roster is empty.' },
  BOT_WALLET_STEP:               { kind: 'readonly', reason: 'Bots now carry real wallets on the Bots admin page. This fallback value only affects synthetic bots that appear if the bot roster is empty.' },

  // ── Free-form numbers ──
  BOT_DICE_CONTROL_DISTANCE_PER_POINT: { kind: 'number', step: 1, min: 0 },
  BOT_CAPTURE_SCORE_BONUS:             { kind: 'number', step: 5,  min: 0 },
  BOT_CHASE_PROXIMITY_BASE:            { kind: 'number', step: 1,  min: 0 },
  BOT_CHASE_HUMAN_MULTIPLIER:          { kind: 'number', step: 0.1, min: 0, max: 5 },
  BOT_FLEE_DANGER_PENALTY_PER:         { kind: 'number', step: 1 },
  BOT_FLEE_HUMAN_EXTRA_PENALTY:        { kind: 'number', step: 1 },
  BOT_FLEE_SAFE_ZONE_BONUS:            { kind: 'number', step: 1,  min: 0 },

  // ── Per-difficulty maps (three labeled sliders) ──
  BOT_TARGET_WINRATE:          { kind: 'difficulty-map', sub: 'percent' },
  BOT_SMART_MOVE_PERCENT:      { kind: 'difficulty-map', sub: 'percent' },
  BOT_CAPTURE_BRUTALITY_BASE:  { kind: 'difficulty-map', sub: 'probability' },
  BOT_WIN_BRUTALITY_BASE:      { kind: 'difficulty-map', sub: 'probability' },
  BOT_BLOCK_BRUTALITY_BASE:    { kind: 'difficulty-map', sub: 'probability' },
  BOT_FORK_BRUTALITY_BASE:     { kind: 'difficulty-map', sub: 'probability' },
  BOT_BLOCK_FORK_BASE:         { kind: 'difficulty-map', sub: 'probability' },
  BOT_STRATEGY_MODE_FACTOR:    { kind: 'difficulty-map', sub: 'number' },

  // Chess Stockfish difficulty knobs — three sliders each, integers.
  // Skill 0–20 is Stockfish's official Skill Level range (0 = nearly
  // random, 20 = full strength). Depth caps how many plies the engine
  // searches per move; higher = stronger but slower. Defaults
  // (3/10/18 skill, 6/12/18 depth) are the seeded values in
  // chess/config/bot.js — admin can tune freely without code changes.
  BOT_STOCKFISH_SKILL_BY_MODE: {
    kind: 'difficulty-map', sub: 'number',
    min: 0, max: 20, step: 1, suffix: '/20', integer: true,
  },
  BOT_STOCKFISH_DEPTH_BY_MODE: {
    kind: 'difficulty-map', sub: 'number',
    min: 1, max: 30, step: 1, suffix: ' ply', integer: true,
  },

  // Centipawn threshold below which an armed quit-when-losing bot
  // resigns. Negative numbers only — −200 ≈ down a minor piece,
  // −500 ≈ down a rook. Less negative = bot quits sooner.
  BOT_QUIT_WHEN_LOSING_THRESHOLD_CP: {
    kind: 'integer', min: -10000, max: 0, suffix: 'cp',
  },

  // ── Complex structures with dedicated safe editors ──
  BOT_MODE_BY_BET:                { kind: 'bet-mode-list' },
  BOT_PERSONALITY_WEIGHTS_BY_BET: { kind: 'personality-weight-rules' },
  BOT_PERSONALITIES_SPEC:         { kind: 'personality-list' },

  // ── Fixed-key maps ──
  BOT_POSITION_WEIGHTS: {
    kind: 'key-value-map',
    keys: [
      { key: 'center',           label: 'Centre cell',           icon: 'target' },
      { key: 'corner',           label: 'Corner cells',          icon: 'filter_none' },
      { key: 'edge',             label: 'Edge cells',            icon: 'border_all' },
      { key: 'opposite_corner',  label: 'Opposite corner bonus', icon: 'swap_horiz' },
    ],
    valueType: 'number',
  },
  // BOT_AVATAR_BY_COLOR / BY_MARK are now fallbacks. Real bots carry
  // their own avatar (uploaded on the per-bot page). These values are
  // only used when a synthetic fallback bot has to be spawned.
  BOT_AVATAR_BY_COLOR: { kind: 'readonly', reason: 'Each real bot now carries its own avatar uploaded on the Bots admin page. These per-colour defaults are only used for synthetic fallback bots.' },
  BOT_AVATAR_BY_MARK:  { kind: 'readonly', reason: 'Each real bot now carries its own avatar uploaded on the Bots admin page. These per-mark defaults are only used for synthetic fallback bots.' },

  // ── Array-of-numbers / strings with row controls ──
  BET_TIERS:     { kind: 'number-list', min: 0, max: 1_000_000, allowZero: true },
  PLAYER_MODES:  {
    kind: 'checkboxes',
    options: [
      { value: 2, label: '1-vs-1 (2 players)' },
      { value: 4, label: '4 players' },
    ],
  },
  // BOT_NAMES is now deprecated — names are stored in the `bots`
  // table and editable on the Bots admin page. Kept as read-only so
  // the previous value is still visible for anyone comparing.
  BOT_NAMES:    { kind: 'readonly', reason: 'Bot names are now managed per-bot in the Bots admin page. This list is only used as a fallback when no real bot is available.' },
  STUN_SERVERS: { kind: 'string-list', placeholder: 'stun:host:port' },

  // ── Read-only (engine-critical, edit at your own risk / not via UI) ──
  TRACK_LENGTH:        { kind: 'readonly', reason: 'Classic Ludo board size — changing breaks board art.' },
  HOME_STRETCH_LENGTH: { kind: 'readonly', reason: 'Classic Ludo home-column length.' },
  START_INDICES:       { kind: 'readonly', reason: 'Standard Ludo start squares per colour.' },
  SAFE_ZONES:          { kind: 'readonly', reason: 'Standard Ludo safe cells. Editing changes capture rules mid-match.' },
  COLOR_ASSIGNMENT:    { kind: 'readonly', reason: 'Standard seat-colour assignment by player count.' },
  BOARD_SIZE:          { kind: 'readonly', reason: 'X&O is always a 3x3 board.' },
  WIN_LINES:           { kind: 'readonly', reason: 'The eight winning triples in a 3x3 grid — do not change.' },
  PLAYER_COUNT:        { kind: 'readonly', reason: 'X&O is always 1-vs-1.' },
  TURN_SERVERS:        { kind: 'readonly', reason: 'Contains credentials — ask engineering to rotate.' },
  // Snake & Ladder — values the game does NOT actually read today (hardcoded),
  // so they're read-only to avoid implying an effect they don't have.
  PLAYERS_PER_MATCH:       { kind: 'readonly', reason: '2P/4P is chosen per-queue from the player\'s lobby tab — this stored value is not read by the game.' },
  TOKEN_PICK_SECONDS:      { kind: 'readonly', reason: 'Token-pick timeout is hardcoded in the game today — not read from this value.' },
  RECONNECT_GRACE_SECONDS: { kind: 'readonly', reason: 'Disconnect tolerance is hardcoded in the game today — not read from this value.' },
  // Reserved keys — placeholders for future features. Editing today
  // has no effect; widget shown as readonly with a clear message.
  // BOARD_SIZE is already declared above with the X&O-specific reason.
  VARIANT:             { kind: 'readonly', reason: 'Reserved for future variants. Only the default is implemented today.' },

  // ── New numeric knobs added in Phase 7 ────────────────────────────
  STALE_SESSION_MAX_HOURS:    { kind: 'integer', min: 1, max: 168, suffix: 'hours' },
  MIN_BET_FOR_BOT_MATCH:      { kind: 'integer', min: 0, max: 1000000, suffix: 'INR (0=no gate)' },

  // ── Plinko (single-player vs house) ──────────────────────────────
  // Plinko keys are lowercase snake_case (the other games use
  // SCREAMING_CASE). Each MANIFEST key gets a typed editor here — note that is no
  // longer the same thing as "each row in plinko_config": Phase F retired eight
  // keys on 2026-09-06 and deliberately left their rows in the table, so eight
  // rows now have no editor by design.
  // Categories: master / bet_sizing / board / multipliers / house_edge / safety.

  // master
  enabled:                              { kind: 'bool' },
  cooldown_ms:                          { kind: 'duration-ms' },
  max_drops_per_minute:                 { kind: 'integer', min: 1, max: 600, suffix: '/min' },
  min_balance_to_play:                  { kind: 'integer', min: 0, max: 10000000, suffix: '₹' },
  daily_max_loss_per_user:              { kind: 'integer', min: 0, max: 10000000, suffix: '₹' },
  session_idle_timeout_ms:              { kind: 'duration-ms' },

  // bet_sizing
  min_bet:                              { kind: 'integer', min: 1, max: 10000000, suffix: '₹' },
  max_bet:                              { kind: 'integer', min: 1, max: 10000000, suffix: '₹' },
  bet_presets:                          { kind: 'number-list', min: 1, max: 10000000 },

  // board
  row_count_options:                    { kind: 'number-list', min: 4, max: 24 },
  default_row_count:                    { kind: 'enum', options: [
                                            { value: '8',  label: '8 rows' },
                                            { value: '12', label: '12 rows' },
                                            { value: '16', label: '16 rows' },
                                          ] },
  risk_levels:                          { kind: 'string-list', placeholder: 'low | medium | high' },
  default_risk:                         { kind: 'enum', options: [
                                            { value: 'low',    label: 'Low (safe, mild wins)',           icon: 'sentiment_satisfied' },
                                            { value: 'medium', label: 'Medium (balanced)',              icon: 'sentiment_neutral' },
                                            { value: 'high',   label: 'High (big jackpots, big losses)', icon: 'local_fire_department' },
                                          ] },

  // multipliers (all 9 are arrays of decimals — center low, edges high)
  multipliers_low_8:                    { kind: 'number-list', min: 0, max: 100000, allowZero: true },
  multipliers_low_12:                   { kind: 'number-list', min: 0, max: 100000, allowZero: true },
  multipliers_low_16:                   { kind: 'number-list', min: 0, max: 100000, allowZero: true },
  multipliers_medium_8:                 { kind: 'number-list', min: 0, max: 100000, allowZero: true },
  multipliers_medium_12:                { kind: 'number-list', min: 0, max: 100000, allowZero: true },
  multipliers_medium_16:                { kind: 'number-list', min: 0, max: 100000, allowZero: true },
  multipliers_high_8:                   { kind: 'number-list', min: 0, max: 100000, allowZero: true },
  multipliers_high_12:                  { kind: 'number-list', min: 0, max: 100000, allowZero: true },
  multipliers_high_16:                  { kind: 'number-list', min: 0, max: 100000, allowZero: true },

  // house_edge
  house_edge_pct:                       { kind: 'number', min: 0, max: 50, step: 0.1 },
  house_edge_pct_low:                   { kind: 'number', min: 0, max: 50, step: 0.1 },
  house_edge_pct_medium:                { kind: 'number', min: 0, max: 50, step: 0.1 },
  house_edge_pct_high:                  { kind: 'number', min: 0, max: 50, step: 0.1 },

  // surprise (anti-rage-quit) — the five surprise_* widgets were REMOVED
  // 2026-09-06 with Plinko's surprise-win override (plan §1.4, Phase F). The
  // manifest no longer declares those keys, so no editor is requested for them.

  // safety
  max_payout_per_drop:                  { kind: 'integer', min: 1, max: 100000000, suffix: '₹' },
  // house_solvency_check's bool widget was REMOVED 2026-09-13 (audit P3): the key
  // was a safety toggle no code path read, so the manifest no longer declares it
  // and no editor is requested for it.

  // ── Added by migration 105 (2026-05-30) ─────────────────────────
  // master — daily_max_win_per_user was REMOVED 2026-09-06 (plan §1.4, Phase F);
  // the daily LOSS cap above it is a responsible-gaming rail and stays.
  maintenance_message:                  { kind: 'string', placeholder: 'e.g. "Be right back — maintenance 9pm–10pm IST"' },
  // safety
  per_user_disable_list:                { kind: 'number-list', min: 1, max: 9999999999, placeholder: 'e.g. 11, 42  (user IDs to block from Plinko)' },
  big_win_alert_threshold:              { kind: 'integer', min: 0, max: 100000000, suffix: '₹ (0 = OFF)' },
  // new_user (first-play protection) — the two new_user_grace_* widgets were
  // REMOVED 2026-09-06 with the grace branch itself (plan §1.4, Phase F).

  // ── Lucky 7 (timed, house-banked 3-card dice betting) ────────────
  // Keys are lowercase snake_case (same convention as Plinko). The few
  // shared key names (enabled, maintenance_message, min_bet, max_bet,
  // bet_presets, per_user_disable_list, big_win_alert_threshold) already
  // have widgets above and are reused as-is.
  // master
  round_interval_seconds:               { kind: 'duration-sec' },
  betting_lock_seconds:                 { kind: 'duration-sec' },
  // betting
  max_bet_per_user_per_round:           { kind: 'integer', min: 1, max: 100000000, suffix: '₹' },
  // roulette: the slip is committed whole, so its caps are per SPIN
  max_bet_per_spin:                     { kind: 'integer', min: 1, max: 100000000, suffix: '₹' },
  max_bets_per_spin:                    { kind: 'integer', min: 1, max: 200 },

  // ── Shared tables + Cash Casino (Phase C/D, contract C4) ─────────
  // These four games deliberately reuse the platform-standard key names for
  // every shared concept (enabled, maintenance_message, min_bet, max_bet,
  // bet_presets, max_round_payout, big_win_alert_threshold,
  // per_user_disable_list, retention_days, history_visible_days,
  // target_profit_*) — those already have widgets above and are inherited with
  // zero work. Only the three genuinely NEW keys need entries; without them
  // ConfigField falls back to the read-only widget and the admin cannot edit
  // them at all.
  //
  // aviator / andar_bahar / teen_patti — the per-round ceiling for ONE player
  // across ALL their placements on that round (both Aviator panels, ANDAR +
  // BAHAR, PLAYER A + PLAYER B). Distinct from max_bet, which caps ONE chip.
  max_bet_per_user_round:               { kind: 'integer', min: 1, max: 100000000, suffix: '₹' },
  // aviator — the highest AUTO CASH OUT multiplier a player may arm. The curve
  // reaches X500; the manifest bounds this at 2..500.
  max_auto_cashout:                     { kind: 'number', min: 2, max: 500, step: 1, suffix: '×' },
  // casino — server-enforced pacing between spins, measured against the DB
  // clock so a second tab cannot spin faster. 0 disables pacing.
  min_spin_interval_ms:                 { kind: 'duration-ms' },
  // payouts
  payout_2to6:                          { kind: 'number', min: 1, max: 100, step: 0.1, suffix: '×' },
  payout_7:                             { kind: 'number', min: 1, max: 100, step: 0.1, suffix: '×' },
  payout_8to12:                         { kind: 'number', min: 1, max: 100, step: 0.1, suffix: '×' },
  // result_engine
  // Lucky 7's six engagement_* widgets were removed on 2026-09-06 with the
  // engagement engine itself (plan docs/ADRENALINE_SHARED_TABLES_PLAN.md §1.4,
  // Phase F — per-user favour moved to the Luck Ledger). The category stays:
  // thimbles still uses it, and so does lucky7's zero_player_mode below.
  zero_player_mode:                     { kind: 'enum', options: [
                                            { value: 'fair_dice', label: 'Fair dice (true 2-dice odds)' },
                                            { value: 'uniform',   label: 'Uniform (each card equal)' },
                                          ] },
  // safety
  max_round_payout:                     { kind: 'integer', min: 0, max: 1000000000, suffix: '₹ (0 = no cap)' },
  // retention
  retention_days:                       { kind: 'integer', min: 2, max: 365, suffix: 'days' },
  history_visible_days:                 { kind: 'integer', min: 1, max: 365, suffix: 'days' },

  // ── Thimbles (house-banked shell game) ──────────────────────────────
  // Shared keys (enabled, maintenance_message, min_bet, max_bet, bet_presets,
  // per_user_disable_list, big_win_alert_threshold, retention_days,
  // history_visible_days) reuse the editors above. Only the Thimbles-specific
  // knobs are declared here.
  pick_timeout_seconds:                 { kind: 'duration-sec' },
  multiplier_single:                    { kind: 'number', min: 1, max: 100, step: 0.01, suffix: '×' },
  multiplier_double:                    { kind: 'number', min: 1, max: 100, step: 0.01, suffix: '×' },
  target_rtp_pct:                       { kind: 'integer', min: 50, max: 100, suffix: '% RTP (lower = bigger house edge)' },
  cool_off_streak:                      { kind: 'integer', min: 0, max: 50, suffix: 'wins (0 = OFF)' },
  cool_off_factor_pct:                  { kind: 'integer', min: 1, max: 100, suffix: '% of win chance while hot' },
  // seven_day_edge_cap_pct / seven_day_cap_min_wagered were RETIRED 2026-09-06
  // with Thimbles' 7-day edge cap (plan §1.4, Phase F). Their manifest entries are
  // gone, so no editor is requested for them any more; leaving the widgets here
  // would only make a dead key look configurable if it were ever re-declared.
  speed_wins_to_max:                    { kind: 'integer', min: 1, max: 100, suffix: 'wins' },
  start_peak_ms:                        { kind: 'integer', min: 20, max: 1000, suffix: 'ms (new-player peak)' },
  max_peak_ms:                          { kind: 'integer', min: 10, max: 1000, suffix: 'ms (fastest ceiling)' },
  post_max_squeeze_pct:                 { kind: 'integer', min: 1, max: 100, suffix: '% per win past max' },
  min_win_chance_pct:                   { kind: 'integer', min: 0, max: 100, suffix: '% floor' },
};

export function widgetFor(key: string): Widget {
  return WIDGET_SCHEMA[key] || { kind: 'readonly', reason: 'This setting has no safe admin editor yet — ask engineering.' };
}

// ─── Category metadata: label + icon + TIER + impact + plain-English blurb ──
// The backend ships `category: 'bot-afk'` style slugs. This map gives each a
// friendly label, a tier (how often an admin actually touches it), a one-line
// blurb (what it controls), and impact tags (what changing it affects). The
// admin panel orders sections by tier — Everyday (1) on top, Advanced (2) in
// the middle, Rarely-changed/Informational (3) tucked at the bottom — so the
// settings you tune day-to-day are never buried under niche bot knobs.
// Missing entries fall through to a safe Tier-2 default so a new category
// never breaks the UI.
export type CatImpact = 'money' | 'live' | 'fairness' | 'cosmetic' | 'structural';
export type CatTier = 1 | 2 | 3;
export type CategoryInfo = {
  icon: string;
  label: string;
  tier: CatTier;
  blurb: string;
  impact: CatImpact[];
  // Sort weight WITHIN a tier — lower comes first (default 0). Used to
  // pin the Target Profit headline to the very top of Tier 1, above the
  // alphabetical run of everyday controls.
  order?: number;
  // TP-7: true for categories whose job the Target Profit engine takes
  // over while it is ON. When target_profit_enabled is true for a game,
  // ConfigPanel marks these "Superseded", mutes them and keeps them
  // collapsed — so the single slider reads as the one control in charge.
  // They stay fully editable (they're the fallback the moment Target
  // Profit is switched back off); nothing here changes when it's OFF.
  supersededByTargetProfit?: boolean;
};

// Section headers shown between tiers in the admin panel.
export const TIER_META: Record<CatTier, { label: string; blurb: string; icon: string }> = {
  1: { label: 'Everyday controls',              blurb: 'The money and on/off levers you actually adjust.',                          icon: 'tune' },
  2: { label: 'Advanced',                       blurb: 'Occasional tuning — change these when you know the effect you want.',       icon: 'settings_suggest' },
  3: { label: 'Rarely changed · informational', blurb: 'Structural, reserved or internal. Mostly for reference — touch only in rare cases.', icon: 'info' },
};

// Per-setting impact chips — "what does changing this risk?".
export const IMPACT_META: Record<CatImpact, { label: string; icon: string; cls: string }> = {
  money:      { label: 'Affects money',        icon: 'paid',    cls: 'text-amber-700 dark:text-amber-300 bg-amber-500/10 border-amber-500/30' },
  live:       { label: 'Affects live matches', icon: 'bolt',    cls: 'text-sky-700 dark:text-sky-300 bg-sky-500/10 border-sky-500/30' },
  fairness:   { label: 'Bot fairness',         icon: 'balance', cls: 'text-violet-700 dark:text-violet-300 bg-violet-500/10 border-violet-500/30' },
  cosmetic:   { label: 'Cosmetic only',        icon: 'palette', cls: 'text-neutral-500 dark:text-neutral-400 bg-neutral-500/10 border-neutral-500/30' },
  structural: { label: 'Structural',           icon: 'build',   cls: 'text-neutral-500 dark:text-neutral-400 bg-neutral-500/10 border-neutral-500/30' },
};

export const CATEGORY_META: Record<string, CategoryInfo> = {
  // ── Tier 1 · Everyday (money + on/off) ────────────────────────────
  target_profit: { icon: 'track_changes',      label: 'Target Profit',          tier: 1, order: -10, impact: ['money'], blurb: 'The single automated control: the % of total user bets this game keeps as profit. Drives difficulty + volatility to hit that target each day. When ON it replaces the edge / bot-strength knobs below.' },
  master:        { icon: 'power_settings_new', label: 'Master switches',        tier: 1, impact: ['live'],  blurb: 'Turn the game on or off and set the maintenance message.' },
  money:         { icon: 'paid',               label: 'Platform fee',           tier: 1, impact: ['money'], blurb: "The platform's cut taken from each match." },
  lobby:         { icon: 'meeting_room',       label: 'Lobby & bet tiers',      tier: 1, impact: ['money'], blurb: 'The bet amounts players can choose from.' },
  bet_sizing:    { icon: 'paid',               label: 'Bet sizing',             tier: 1, impact: ['money'], blurb: 'Smallest and largest stake a player can bet.' },
  betting:       { icon: 'casino',             label: 'Bet sizing',             tier: 1, impact: ['money'], blurb: 'Smallest and largest stake a player can bet.' },
  payouts:       { icon: 'paid',               label: 'Payout multipliers',     tier: 1, impact: ['money'], blurb: 'How much winners are paid for each card.' },
  multipliers:   { icon: 'casino',             label: 'Multiplier tables',      tier: 1, impact: ['money'], blurb: 'Payout tables for each risk level × row count.' },
  house_edge:    { icon: 'trending_down',      label: 'House edge',             tier: 1, impact: ['money'], supersededByTargetProfit: true, blurb: "The platform's built-in margin on every bet." },
  safety:        { icon: 'shield',             label: 'Safety caps',            tier: 1, impact: ['money'], blurb: 'Caps that protect the platform from large single-game losses.' },
  'bot-master':  { icon: 'power_settings_new', label: 'Bots on/off',            tier: 1, impact: ['live'],  blurb: 'Whether bots are used to fill matches at all.' },
  rules:         { icon: 'rule',               label: 'Match rules',            tier: 1, impact: ['live'],  blurb: 'Core rules of how the game is played.' },

  // ── Tier 2 · Advanced (occasional tuning) ─────────────────────────
  timing:        { icon: 'schedule',           label: 'Turn timers',            tier: 2, impact: ['live'],         blurb: 'Turn timers and round windows.' },
  kicks:         { icon: 'gavel',              label: 'Auto-kick idle players', tier: 2, impact: ['live'],         blurb: 'When idle or disconnected players are auto-removed.' },
  result_engine: { icon: 'psychology',         label: 'Result engine',          tier: 2, impact: ['money', 'live'], supersededByTargetProfit: true, blurb: 'How winning results are decided across player counts + engagement.' },
  // The `surprise` ("Surprise wins") and `new_user` ("New-user grace") sections
  // were REMOVED 2026-09-06 (plan §1.4, Phase F). Plinko was their only member and
  // both mechanics are retired, so no manifest key carries these category slugs
  // any more and the two collapsible sections no longer exist in the admin panel.
  // (ConfigPanel falls back to a generic folder header for an unknown slug, so a
  // future key reusing either name still renders — just without this styling.)
  voice:         { icon: 'mic',                label: 'Voice chat',             tier: 2, impact: [],               blurb: 'In-match voice chat.' },
  'bot-queue':   { icon: 'groups',             label: 'When bots fill seats',   tier: 2, impact: ['live'],         blurb: 'When bots step in to fill empty seats.' },
  'bot-fill':    { icon: 'groups',             label: 'When bots fill seats',   tier: 2, impact: ['live'],         blurb: 'When bots step in to fill empty seats.' },
  'bot-diff':    { icon: 'insights',           label: 'Bot difficulty',         tier: 2, impact: ['fairness'], supersededByTargetProfit: true,    blurb: 'How strong the bots play.' },
  'bot-dice':    { icon: 'casino',             label: 'Bot dice (kept fair)',   tier: 2, impact: ['fairness'], supersededByTargetProfit: true,    blurb: 'Bot dice behaviour — never rigged against real players.' },
  'bot-brutal':  { icon: 'local_fire_department', label: 'How ruthless bots play', tier: 2, impact: ['fairness'], supersededByTargetProfit: true, blurb: 'How aggressively the bots play.' },
  'bot-ai':      { icon: 'smart_toy',          label: 'Bot AI & smarts',        tier: 2, impact: ['fairness'], supersededByTargetProfit: true,    blurb: 'Bot decision-making and smarts.' },
  bot:           { icon: 'smart_toy',          label: 'Bot engine',             tier: 2, impact: ['fairness'],     blurb: 'Core bot engine settings.' },
  'bot-score':   { icon: 'calculate',          label: 'Bot scoring weights',    tier: 2, impact: ['fairness'],     blurb: 'Internal weights the bot uses to score its moves.' },
  'bot-feel':    { icon: 'psychology',         label: 'Making bots feel human', tier: 2, impact: [],               blurb: 'Tweaks that make bots feel human.' },
  'bot-human':   { icon: 'psychology',         label: 'Making bots feel human', tier: 2, impact: [],               blurb: 'Tweaks that make bots feel human.' },
  'bot-behaviour':{ icon: 'psychology',        label: 'Bot behaviour',          tier: 2, impact: ['fairness'], supersededByTargetProfit: true, blurb: 'General bot behaviour tuning.' },
  'bot-pacing':  { icon: 'speed',              label: 'Bot pacing',             tier: 2, impact: [],               blurb: 'Bot turn timing and pacing.' },
  'bot-speed':   { icon: 'speed',              label: 'How fast bots act',      tier: 2, impact: [],               blurb: 'How quickly bots take their turns.' },
  'bot-afk':     { icon: 'person_off',         label: 'Bot walks away',         tier: 2, impact: [],               blurb: 'How often a bot acts idle or steps away.' },
  'bot-online':  { icon: 'wifi',               label: 'Bot online presence',    tier: 2, impact: [],               blurb: 'Bot online-presence behaviour.' },
  retention:     { icon: 'auto_delete',        label: 'History retention',      tier: 2, impact: [],               blurb: 'How long played-round history is kept before pruning.' },

  // ── Tier 3 · Rarely changed / informational (structural, reserved) ─
  geometry:      { icon: 'grid_view',          label: 'Board layout',           tier: 3, impact: ['structural'], blurb: 'Board layout — structural, set once. Mostly read-only.' },
  board:         { icon: 'grid_view',          label: 'Board shape',            tier: 3, impact: ['structural'], blurb: 'Board shape — structural, set once.' },
  physics:       { icon: 'sports_handball',    label: 'Physics constants',      tier: 3, impact: ['structural'], blurb: 'Carrom physics constants — structural.' },
  pockets:       { icon: 'adjust',             label: 'Pocket geometry',        tier: 3, impact: ['structural'], blurb: 'Carrom pocket geometry — structural.' },
  match:         { icon: 'tag',                label: 'Match shape',            tier: 3, impact: ['structural'], blurb: 'Players/tokens per match — mostly reserved/read-only.' },
  network:       { icon: 'router',             label: 'Network / connection',   tier: 3, impact: ['structural'], blurb: 'Connection and networking internals.' },
  audit:         { icon: 'history',            label: 'Audit & logging',        tier: 3, impact: ['structural'], blurb: 'Audit and logging internals.' },
  'bot-cosmetic':{ icon: 'face',               label: 'Bot names & avatars',    tier: 3, impact: ['cosmetic'],   blurb: 'Bot display names and avatars.' },
  'bot-identity':{ icon: 'badge',              label: 'Bot identities',         tier: 3, impact: ['cosmetic'],   blurb: 'Bot identity and profiles — internal.' },
  'bot-drift':   { icon: 'trending_up',        label: '"Players online" counter', tier: 3, impact: ['cosmetic'], blurb: 'The fake "players online" counter behaviour.' },
  'bot-roster':  { icon: 'badge',              label: 'Bot roster & wallets',   tier: 3, impact: ['structural'], blurb: 'Bot roster and wallets — internal.' },
};
